import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useFiscalYear } from '../contexts/FiscalYearContext';

/**
 * MTD Purchases Summary — small dashboard widget that gives accountants a
 * quick "did we capture everything?" check before pulling the Bahrain VAT
 * Return. Shows month-to-date totals from two parallel inputs:
 *   • Purchase Entries (formal goods receipts against POs)
 *   • Other Purchases  (assets / office expenses / utilities, no PO)
 */
const fmt = (n) =>
  Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

const MTDPurchasesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { selectedFy } = useFiscalYear();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const params = {};
        if (selectedFy) {
          params.fy_start_date = selectedFy.start_date;
          params.fy_end_date = selectedFy.end_date;
        }
        const res = await api.get('/dashboard/mtd-purchases-summary', { params });
        if (alive) setData(res.data);
      } catch (e) {
        // Silent — widget is optional
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedFy?.id]);

  if (loading || !data) return null;

  const pe = data.purchase_entries || {};
  const op = data.other_purchases || {};
  const combined = data.combined || {};
  const ytd = data.fiscal_year_to_date || null;

  return (
    <Card
      data-testid="mtd-purchases-widget"
      className="border-emerald-200 mb-4 sm:mb-6"
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <span aria-hidden>🧾</span> MTD Purchases — VAT Capture Check
          </CardTitle>
          <Badge variant="outline" className="text-xs" data-testid="mtd-period-label">
            {data.month_label} · {data.period_start} → {data.period_end}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Purchase Entries */}
          <div
            data-testid="mtd-purchase-entries"
            className="rounded-lg border border-blue-200 bg-blue-50/40 px-3 py-2"
          >
            <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold">
              Purchase Entries
            </div>
            <div className="flex items-end justify-between mt-1">
              <div>
                <div className="text-xs text-gray-600">Count</div>
                <div
                  className="text-lg font-bold text-blue-900"
                  data-testid="mtd-pe-count"
                >
                  {pe.count || 0}
                  {pe.vatable_count !== undefined && pe.vatable_count !== pe.count && (
                    <span className="text-xs text-gray-500 font-normal ml-1">
                      ({pe.vatable_count} VATable)
                    </span>
                  )}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600">Net / VAT (BHD)</div>
                <div className="text-sm font-semibold text-blue-900" data-testid="mtd-pe-net">
                  {fmt(pe.net_bhd)}
                </div>
                <div className="text-xs text-blue-700" data-testid="mtd-pe-vat">
                  VAT {fmt(pe.vat_bhd)}
                </div>
              </div>
            </div>
          </div>

          {/* Other Purchases */}
          <div
            data-testid="mtd-other-purchases"
            className="rounded-lg border border-amber-200 bg-amber-50/40 px-3 py-2"
          >
            <div className="text-xs uppercase tracking-wide text-amber-700 font-semibold">
              Other Purchases
            </div>
            <div className="flex items-end justify-between mt-1">
              <div>
                <div className="text-xs text-gray-600">Count</div>
                <div
                  className="text-lg font-bold text-amber-900"
                  data-testid="mtd-op-count"
                >
                  {op.count || 0}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600">Net / VAT (BHD)</div>
                <div className="text-sm font-semibold text-amber-900" data-testid="mtd-op-net">
                  {fmt(op.net_bhd)}
                </div>
                <div className="text-xs text-amber-700" data-testid="mtd-op-vat">
                  VAT {fmt(op.vat_bhd)}
                </div>
              </div>
            </div>
          </div>

          {/* Combined */}
          <div
            data-testid="mtd-combined"
            className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2"
          >
            <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">
              Combined MTD
            </div>
            <div className="flex items-end justify-between mt-1">
              <div>
                <div className="text-xs text-gray-600">Records</div>
                <div
                  className="text-lg font-bold text-emerald-900"
                  data-testid="mtd-combined-count"
                >
                  {combined.count || 0}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-600">Net / VAT (BHD)</div>
                <div
                  className="text-sm font-semibold text-emerald-900"
                  data-testid="mtd-combined-net"
                >
                  {fmt(combined.net_bhd)}
                </div>
                <div
                  className="text-xs text-emerald-700"
                  data-testid="mtd-combined-vat"
                >
                  VAT {fmt(combined.vat_bhd)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Fiscal-Year-to-Date strip — only when the global FY selector is active. */}
        {ytd && (
          <div
            className="mt-3 rounded-lg border border-indigo-200 bg-indigo-50/60 px-3 py-2 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3"
            data-testid="mtd-ytd-row"
          >
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-wide text-indigo-700 font-semibold">
                {selectedFy?.name || 'FY'} · to-date
              </span>
              <span className="text-[10px] text-gray-500" data-testid="mtd-ytd-label">
                {ytd.label}
              </span>
            </div>
            <div className="flex items-center justify-between sm:justify-start gap-3 text-sm">
              <span className="text-xs text-gray-600">Records</span>
              <span className="font-semibold text-indigo-900" data-testid="mtd-ytd-count">
                {ytd.combined?.count || 0}
              </span>
            </div>
            <div className="flex items-center justify-between sm:justify-end gap-3 text-sm">
              <span className="text-xs text-gray-600">Net / VAT (BHD)</span>
              <span className="font-semibold text-indigo-900" data-testid="mtd-ytd-net">
                {fmt(ytd.combined?.net_bhd)}
              </span>
              <span className="text-xs text-indigo-700" data-testid="mtd-ytd-vat">
                VAT {fmt(ytd.combined?.vat_bhd)}
              </span>
            </div>
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          <a
            href="/purchase-entries"
            data-testid="mtd-link-purchase-entries"
            className="text-blue-700 hover:underline"
          >
            Open Purchase Entries →
          </a>
          <span className="text-gray-300">·</span>
          <a
            href="/other-purchases"
            data-testid="mtd-link-other-purchases"
            className="text-amber-700 hover:underline"
          >
            Open Other Purchases →
          </a>
          <span className="text-gray-300">·</span>
          <a
            href="/vat-return"
            data-testid="mtd-link-vat-return"
            className="text-emerald-700 hover:underline"
          >
            Generate VAT Return →
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

export default MTDPurchasesWidget;
